import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/auth';
import {
  AuthorizationContext,
  type AuthorizationContextValue,
} from '@/authorization/AuthorizationContext';
import { evaluatePermission } from '@/authorization/PermissionEvaluator';
import { resourceRegistry } from '@/authorization/ResourceRegistry';
import { resolveDirectRoles } from '@/authorization/RoleResolver';
import { authorizationService } from '@/authorization/AuthorizationService';
import { useAppShell } from '@/shell/useAppShell';
import type {
  Permission,
  PermissionAction,
  PermissionResourceKey,
  Role,
} from '@/authorization/types';

interface AuthorizationProviderProps {
  children: ReactNode;
}

const immutableSystemRoleRules: Array<{
  resource: PermissionResourceKey;
  action: PermissionAction;
}> = [
  { resource: 'roles', action: 'admin' },
  { resource: 'users', action: 'admin' },
  { resource: 'systemSettings', action: 'admin' },
];

const permissionAliasMap: Record<string, string> = {
  USER_VIEW: 'USERS_READ',
  USER_CREATE: 'USERS_CREATE',
  USER_EDIT: 'USERS_UPDATE',
  USER_DELETE: 'USERS_DELETE',
  USER_ASSIGN_ROLE: 'USERS_ADMIN',
  USER_ASSIGN_PRODUCT: 'USERS_ADMIN',
  USER_ASSIGN_GROUP: 'USERS_ADMIN',
  GROUP_VIEW: 'GROUPS_READ',
  GROUP_CREATE: 'GROUPS_CREATE',
  GROUP_EDIT: 'GROUPS_UPDATE',
  GROUP_DELETE: 'GROUPS_DELETE',
  GROUP_ASSIGN_USERS: 'GROUPS_ADMIN',
  GROUP_ASSIGN_ROLES: 'GROUPS_ADMIN',
  GROUP_ASSIGN_PRODUCTS: 'GROUPS_ADMIN',
  ROLE_VIEW: 'ROLES_READ',
  ROLE_CREATE: 'ROLES_CREATE',
  ROLE_EDIT: 'ROLES_UPDATE',
  ROLE_DELETE: 'ROLES_DELETE',
  PRODUCT_VIEW: 'PRODUCTS_READ',
  PRODUCT_CREATE: 'PRODUCTS_CREATE',
  PRODUCT_EDIT: 'PRODUCTS_UPDATE',
  PRODUCT_DELETE: 'PRODUCTS_DELETE',
  PRODUCT_DEPLOY: 'PRODUCTS_ADMIN',
  PRODUCT_ASSIGN_ROLE: 'PRODUCTS_ADMIN',
  MENU_VIEW: 'PRODUCT_MENU_CONFIGURATION_READ',
  MENU_CREATE: 'PRODUCT_MENU_CONFIGURATION_CREATE',
  MENU_EDIT: 'PRODUCT_MENU_CONFIGURATION_UPDATE',
  MENU_DELETE: 'PRODUCT_MENU_CONFIGURATION_DELETE',
  MENU_REORDER: 'PRODUCT_MENU_CONFIGURATION_UPDATE',
  MENU_ASSIGN_PERMISSION: 'PRODUCT_MENU_CONFIGURATION_ADMIN',
  MENU_ASSIGN_FEATUREFLAG: 'PRODUCT_MENU_CONFIGURATION_ADMIN',
  VIEW_METRICS: 'ADMIN_PORTAL_ACCESS',
  VIEW_ACTIVITY: 'ADMIN_PORTAL_ACCESS',
  VIEW_HEALTH: 'ADMIN_PORTAL_ACCESS',
  VIEW_QUICK_ACTIONS: 'ADMIN_PORTAL_ACCESS',
  NOTIFICATIONS_VIEW: 'NOTIFICATIONS_READ',
  AUDIT_VIEW: 'AUDIT_LOGS_READ',
  AUDIT_EXPORT: 'AUDIT_LOGS_ADMIN',
  AUDIT_DETAILS: 'AUDIT_LOGS_READ',
  AUDIT_ADMIN: 'AUDIT_LOGS_ADMIN',
  FEATURE_FLAGS_VIEW: 'FEATURE_FLAGS_READ',
  FEATUREFLAG_VIEW: 'FEATURE_FLAGS_READ',
  FEATUREFLAG_CREATE: 'FEATURE_FLAGS_CREATE',
  FEATUREFLAG_EDIT: 'FEATURE_FLAGS_UPDATE',
  FEATUREFLAG_DELETE: 'FEATURE_FLAGS_DELETE',
  FEATUREFLAG_DEPLOY: 'FEATURE_FLAGS_ADMIN',
  FEATUREFLAG_OVERRIDE: 'FEATURE_FLAGS_ADMIN',
  SYSTEM_SETTINGS_VIEW: 'SYSTEM_SETTINGS_READ',
  SYSTEM_VIEW: 'SYSTEM_SETTINGS_READ',
  SYSTEM_EDIT: 'SYSTEM_SETTINGS_UPDATE',
  SYSTEM_BRANDING: 'SYSTEM_SETTINGS_ADMIN',
  SYSTEM_SECURITY: 'SYSTEM_SETTINGS_ADMIN',
  SYSTEM_NOTIFICATION: 'SYSTEM_SETTINGS_ADMIN',
  API_CONFIGURATION_VIEW: 'API_CONFIGURATION_READ',
  API_VIEW: 'API_CONFIGURATION_READ',
  API_CREATE: 'API_CONFIGURATION_ADMIN',
  API_EDIT: 'API_CONFIGURATION_ADMIN',
  API_DELETE: 'API_CONFIGURATION_ADMIN',
  API_ROTATE_KEYS: 'API_CONFIGURATION_ADMIN',
  API_WEBHOOK_MANAGE: 'API_CONFIGURATION_ADMIN',
  API_RATE_LIMIT_EDIT: 'API_CONFIGURATION_ADMIN',
  DOCUMENTATION_VIEW: 'DOCUMENTATION_HUB_READ',
  DOCUMENTATION_ADMIN: 'DOCUMENTATION_HUB_UPDATE',
  DOCUMENTATION_EDIT: 'DOCUMENTATION_HUB_UPDATE',
  SUPPORT_CREATE: 'DOCUMENTATION_HUB_READ',
  EMBEDDED_APPLICATIONS_VIEW: 'EMBEDDED_APPLICATIONS_READ',
};

function normalizePermissionKey(permission: string): string {
  return permission.trim().toUpperCase();
}

function toPermissionToken(
  resource: PermissionResourceKey,
  action: PermissionAction,
): string {
  const normalizedResource = resource
    .replace(/([A-Z])/g, '_$1')
    .replace(/-/g, '_')
    .toUpperCase();

  return `${normalizedResource}_${action.toUpperCase()}`;
}

function canMutateSystemPermission(
  role: Role,
  permission: Permission,
): boolean {
  if (!role.isSystemRole) {
    return true;
  }

  return !immutableSystemRoleRules.some(
    (rule) =>
      rule.resource === permission.resource &&
      rule.action === permission.action,
  );
}

export function AuthorizationProvider({
  children,
}: AuthorizationProviderProps) {
  const { status, user } = useAuth();
  const { userProfile } = useAppShell();
  const { pathname } = useLocation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoleId, setSelectedRoleIdState] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const defaultRolesRef = useRef<Map<string, Permission[]>>(new Map());

  const refreshRoles = useCallback(async () => {
    setIsLoading(true);

    try {
      const loadedRoles = await authorizationService.getRoles();
      setRoles(loadedRoles);

      defaultRolesRef.current = new Map(
        loadedRoles.map((role) => [
          role.id,
          role.permissions.map((permission) => ({ ...permission })),
        ]),
      );

      setSelectedRoleIdState((current) => {
        if (current && loadedRoles.some((role) => role.id === current)) {
          return current;
        }

        return loadedRoles[0]?.id ?? null;
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const isAdminRoute = pathname.startsWith('/admin');

    if (status !== 'authenticated' || !isAdminRoute) {
      setRoles([]);
      setSelectedRoleIdState(null);
      setIsLoading(false);
      return;
    }

    void refreshRoles();
  }, [pathname, refreshRoles, status]);

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId) ?? null,
    [roles, selectedRoleId],
  );

  const effectiveUser = useMemo(() => {
    if (!user) {
      return null;
    }

    if (userProfile) {
      return {
        ...user,
        roles: userProfile.roles ?? [],
      };
    }

    return user;
  }, [user, userProfile]);

  const resolvedPermissions = useMemo(() => {
    const directRoles = resolveDirectRoles(effectiveUser, roles);

    const permissionTokens = new Set<string>();

    directRoles.forEach((role) => {
      role.permissions.forEach((permission) => {
        if (!permission.allowed) {
          return;
        }

        permissionTokens.add(
          toPermissionToken(permission.resource, permission.action),
        );
      });
    });

    userProfile?.permissions.forEach((permission) => {
      permissionTokens.add(permission);
    });

    return permissionTokens;
  }, [effectiveUser, roles, userProfile?.permissions]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      const normalizedPermission = normalizePermissionKey(permission);

      if (resolvedPermissions.has('*')) {
        return true;
      }

      if (resolvedPermissions.has(normalizedPermission)) {
        return true;
      }

      const mappedPermission = permissionAliasMap[normalizedPermission];

      if (mappedPermission && resolvedPermissions.has(mappedPermission)) {
        return true;
      }

      return false;
    },
    [resolvedPermissions],
  );

  const canAccess = useCallback(
    (resource: PermissionResourceKey, action: PermissionAction): boolean => {
      if (resolvedPermissions.has('*')) {
        return true;
      }

      const requiredToken = toPermissionToken(resource, action);
      if (resolvedPermissions.has(requiredToken)) {
        return true;
      }

      const directRoles = resolveDirectRoles(effectiveUser, roles);
      const directPermissions = directRoles.flatMap((role) => role.permissions);

      const featureFlags = new Set(
        resourceRegistry
          .map((item) => item.featureFlag)
          .filter((value): value is string => Boolean(value)),
      );

      return evaluatePermission(directPermissions, resource, action, {
        featureFlags,
      });
    },
    [effectiveUser, resolvedPermissions, roles],
  );

  const setSelectedRoleId = useCallback((roleId: string) => {
    setSelectedRoleIdState(roleId);
  }, []);

  const updatePermission = useCallback(
    (
      roleId: string,
      resource: PermissionResourceKey,
      action: PermissionAction,
      allowed: boolean,
    ) => {
      setRoles((current) =>
        current.map((role) => {
          if (role.id !== roleId) {
            return role;
          }

          return {
            ...role,
            permissions: role.permissions.map((permission) => {
              if (
                permission.resource !== resource ||
                permission.action !== action ||
                !canMutateSystemPermission(role, permission)
              ) {
                return permission;
              }

              return {
                ...permission,
                allowed,
                inherited: false,
              };
            }),
            lastModified: new Date().toISOString(),
          };
        }),
      );
    },
    [],
  );

  const setAllForRole = useCallback((roleId: string, allowed: boolean) => {
    setRoles((current) =>
      current.map((role) => {
        if (role.id !== roleId) {
          return role;
        }

        return {
          ...role,
          permissions: role.permissions.map((permission) =>
            canMutateSystemPermission(role, permission)
              ? { ...permission, allowed, inherited: false }
              : permission,
          ),
          lastModified: new Date().toISOString(),
        };
      }),
    );
  }, []);

  const grantAll = useCallback(
    (roleId: string) => {
      setAllForRole(roleId, true);
    },
    [setAllForRole],
  );

  const revokeAll = useCallback(
    (roleId: string) => {
      setAllForRole(roleId, false);
    },
    [setAllForRole],
  );

  const resetRolePermissions = useCallback((roleId: string) => {
    const defaults = defaultRolesRef.current.get(roleId);

    if (!defaults) {
      return;
    }

    setRoles((current) =>
      current.map((role) =>
        role.id === roleId
          ? {
              ...role,
              permissions: defaults.map((permission) => ({ ...permission })),
              lastModified: new Date().toISOString(),
            }
          : role,
      ),
    );
  }, []);

  const toggleResourceRow = useCallback(
    (roleId: string, resource: PermissionResourceKey, allowed: boolean) => {
      setRoles((current) =>
        current.map((role) => {
          if (role.id !== roleId) {
            return role;
          }

          return {
            ...role,
            permissions: role.permissions.map((permission) => {
              if (
                permission.resource !== resource ||
                !canMutateSystemPermission(role, permission)
              ) {
                return permission;
              }

              return {
                ...permission,
                allowed,
                inherited: false,
              };
            }),
            lastModified: new Date().toISOString(),
          };
        }),
      );
    },
    [],
  );

  const toggleActionColumn = useCallback(
    (roleId: string, action: PermissionAction, allowed: boolean) => {
      setRoles((current) =>
        current.map((role) => {
          if (role.id !== roleId) {
            return role;
          }

          return {
            ...role,
            permissions: role.permissions.map((permission) => {
              if (
                permission.action !== action ||
                !canMutateSystemPermission(role, permission)
              ) {
                return permission;
              }

              return {
                ...permission,
                allowed,
                inherited: false,
              };
            }),
            lastModified: new Date().toISOString(),
          };
        }),
      );
    },
    [],
  );

  const value: AuthorizationContextValue = useMemo(
    () => ({
      roles,
      selectedRoleId,
      selectedRole,
      resolvedPermissions,
      isLoading,
      hasPermission,
      canAccess,
      setSelectedRoleId,
      updatePermission,
      grantAll,
      revokeAll,
      resetRolePermissions,
      toggleResourceRow,
      toggleActionColumn,
      refreshRoles,
    }),
    [
      canAccess,
      grantAll,
      hasPermission,
      isLoading,
      refreshRoles,
      resetRolePermissions,
      resolvedPermissions,
      revokeAll,
      roles,
      selectedRole,
      selectedRoleId,
      setSelectedRoleId,
      toggleActionColumn,
      toggleResourceRow,
      updatePermission,
    ],
  );

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
}
